import { Link } from "@inertiajs/react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Sparkles, Image as ImageIcon } from "lucide-react";

interface Photo {
  id: number;
  photoUrl: string;
  analyzed: boolean;
  submissionsCount: number;
  createdAt: string;
}

interface Props {
  photos: Photo[];
  user: { email: string };
}

export default function Index({ photos, user }: Props) {
  return (
    <Layout title="Minhas Fotos" user={user}>
      <div className="mx-auto max-w-6xl px-4 py-10">
        <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
              Minhas Fotos
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Gerencie suas fotos e veja análises de IA
            </p>
          </div>
          <Button asChild className="gap-2">
            <Link href="/submissions/new">
              <Plus size={16} /> Nova Foto
            </Link>
          </Button>
        </header>

        {photos.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {photos.map((photo) => (
              <PhotoCard key={photo.id} photo={photo} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function PhotoCard({ photo }: { photo: Photo }) {
  return (
    <Link href={`/photos/${photo.id}`}>
      <Card className="group relative border-border bg-card transition-all hover:border-border/80 hover:bg-card/80">
        <div className="relative flex items-center justify-center bg-background">
          <img
            src={photo.photoUrl}
            alt="Foto"
            className="w-full transition-transform group-hover:scale-105"
          />
          {photo.analyzed && (
            <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-purple-500/80 px-2 py-0.5 text-xs font-medium text-white backdrop-blur">
              <Sparkles size={10} />
              IA
            </span>
          )}
        </div>
        <div className="px-3 py-2">
          <p className="text-xs text-muted-foreground">
            {photo.submissionsCount === 0
              ? "Sem votações"
              : `${photo.submissionsCount} votação${photo.submissionsCount > 1 ? "ões" : ""}`}
          </p>
        </div>
      </Card>
    </Link>
  );
}

function EmptyState() {
  return (
    <Card className="mt-10 border-border bg-card text-center">
      <div className="flex flex-col items-center gap-4 p-8">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <ImageIcon size={28} className="text-muted-foreground" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Nenhuma foto ainda</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Envie sua primeira foto para receber análises de IA e votações.
          </p>
        </div>
        <Button asChild className="gap-2">
          <Link href="/submissions/new">
            <Plus size={16} /> Nova Foto
          </Link>
        </Button>
      </div>
    </Card>
  );
}
